import { useMemo } from "react";
import { RefreshCw } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { StatCard } from "@/components/data-display/stat-card";
import { DataSourceBadge } from "@/components/data-display/data-source-badge";
import { ApprovalCard, ApprovedRow } from "@/features/approvals/approval-card";
import { useApprovals } from "@/hooks/use-approvals";

export default function ApprovalsPage() {
  const { pending, completed, loading, error, source, refresh } = useApprovals();

  const assigned = useMemo(() => pending.slice(0, 3), [pending]);
  const escalated = useMemo(() => pending.filter((a) => a.urgent), [pending]);

  const stats = useMemo(() => [
    { value: String(assigned.length), label: "Awaiting My Action" },
    { value: pending.length > 0 ? "—" : "0", label: "Avg Response Time" },
    { value: "0", label: "SLA Breaches (7d)" },
  ], [assigned, pending]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="spinner" aria-label="Loading approvals" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-danger-border bg-danger-soft px-4 py-6 text-center text-sm text-danger">
        Failed to load approvals: {error}
        <button type="button" onClick={refresh} className="ml-2 underline">Retry</button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex items-center gap-2">
        <DataSourceBadge source={source} />
        <button
          type="button"
          onClick={refresh}
          className="inline-flex items-center gap-1.5 rounded-md border border-line bg-surface px-2.5 py-1.5 text-xs font-medium text-muted hover:text-ink"
          aria-label="Refresh approvals"
        >
          <RefreshCw className="size-3.5" aria-hidden />
        </button>
      </div>

      <Tabs defaultValue="assigned">
        <TabsList>
          <TabsTrigger value="assigned" count={assigned.length}>Assigned to Me</TabsTrigger>
          <TabsTrigger value="all-pending" count={pending.length}>All Pending</TabsTrigger>
          <TabsTrigger value="completed" count={completed.length} countMuted>Completed</TabsTrigger>
          <TabsTrigger value="escalated" count={escalated.length} countMuted>Escalated</TabsTrigger>
        </TabsList>

        <TabsContent value="assigned" forceMount>
          <div className="mb-5 grid grid-cols-3 gap-3">
            {stats.map((s) => (
              <StatCard key={s.label} {...s} />
            ))}
          </div>
          <div className="flex flex-col gap-3">
            {assigned.map((a) => (
              <ApprovalCard key={a.id} approval={a} />
            ))}
            {assigned.length === 0 && (
              <div className="rounded-lg border border-dashed border-line px-4 py-10 text-center text-sm text-muted">
                No approvals assigned to you right now.
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="all-pending" forceMount>
          <div className="mb-5 grid grid-cols-3 gap-3">
            {stats.map((s) => (
              <StatCard key={s.label} {...s} />
            ))}
          </div>
          <div className="flex flex-col gap-3">
            {pending.map((a) => (
              <ApprovalCard key={a.id} approval={a} />
            ))}
            {pending.length === 0 && (
              <div className="rounded-lg border border-dashed border-line px-4 py-10 text-center text-sm text-muted">
                No pending approvals.
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="completed" forceMount>
          <div className="rounded-lg border border-line bg-surface">
            {completed.map((a) => (
              <ApprovedRow key={a.id} approval={a} />
            ))}
            {completed.length === 0 && (
              <div className="px-4 py-10 text-center text-sm text-muted">
                No completed approvals yet.
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="escalated" forceMount>
          <div className="flex flex-col gap-3">
            {escalated.map((a) => (
              <ApprovalCard key={a.id} approval={a} />
            ))}
            {escalated.length === 0 && (
              <div className="rounded-lg border border-dashed border-line px-4 py-10 text-center text-sm text-muted">
                No escalated approvals.
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
